const BASE = '/api/v1';

export type SessionStage = 'pending_mfa' | 'pending_passkey_enrollment' | 'active';
export type TaskStatus = 'pending' | 'in_progress' | 'done' | 'blocked';

export type AuthUser = {
  id: string;
  username: string;
  role: string;
};

export type AuthState = {
  authenticated: boolean;
  authType: 'user' | 'service' | null;
  bootstrapRequired: boolean;
  sessionStage: SessionStage | null;
  user: AuthUser | null;
  agent: string | null;
};

export type ApiResponse<T> = {
  data: T;
  meta?: {
    total?: number;
    nextCursor?: string;
    hasMore?: boolean;
  };
};

export type CategoryRecord = {
  id: string;
  name: string;
  icon: string | null;
  color: string | null;
  sortOrder?: number | null;
};

export type TaskRecord = {
  id: string;
  title: string;
  status: TaskStatus;
  priority: number;
  categoryId: string | null;
  categoryName: string | null;
  categoryIcon: string | null;
  categoryColor: string | null;
  notes: string | null;
  dueDate: string | null;
  dueTime: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  percentDone: number | null;
  completedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type TaskCreatePayload = {
  title: string;
  category_id?: string | null;
  status?: TaskStatus;
  priority?: number;
  notes?: string | null;
  due_date?: string | null;
  due_time?: string | null;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  percent_done?: number;
};

export type TaskUpdatePayload = Partial<TaskCreatePayload>;

export type EventRecord = {
  id: string;
  title: string;
  description: string | null;
  eventDate: string;
  startTime: string | null;
  endTime: string | null;
  allDay: boolean;
  color: string | null;
  taskId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type EventWritePayload = {
  title: string;
  description?: string | null;
  event_date: string;
  start_time?: string | null;
  end_time?: string | null;
  all_day?: boolean;
  color?: string | null;
  task_id?: string | null;
};

export type DailyNoteRecord = {
  id: string;
  userId: string;
  noteDate: string;
  title: string | null;
  content: string;
  createdAt: string | null;
  updatedAt: string | null;
};

export type DailyNoteWritePayload = {
  title?: string;
  content?: string;
};

type AuthBootstrapResponse = ApiResponse<{
  requiresPasskeyEnrollment: boolean;
  sessionStage: SessionStage;
  user: AuthUser;
}>;

type AuthPasswordResponse = ApiResponse<{
  sessionStage: SessionStage;
  requiresPasskey: boolean;
  requiresPasskeyEnrollment: boolean;
  user: AuthUser;
}>;

type AuthPasskeyVerificationResponse = ApiResponse<{
  authenticated: boolean;
  user: AuthUser;
  passkeyAdded?: boolean;
  sessionStage: SessionStage;
}>;

export type PasskeyInfo = {
  id: string;
  name: string | null;
  deviceType: string;
  backedUp: boolean;
  createdAt: string | null;
  lastUsedAt: string | null;
};

type DeleteResponse = ApiResponse<{ id: string; deleted: true }>;

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const raw = await res.text();
  let data: (T & { error?: { message?: string } }) | null = null;

  if (raw) {
    try {
      data = JSON.parse(raw) as T & { error?: { message?: string } };
    } catch {
      if (!res.ok) {
        throw new Error(raw);
      }

      throw new Error('Received a non-JSON response from the server');
    }
  }

  if (!res.ok) {
    throw new Error(data?.error?.message || raw || `Request failed (${res.status})`);
  }

  if (!data) {
    throw new Error('Received an empty response from the server');
  }

  return data;
}

export const api = {
  tasks: {
    list: (params?: Record<string, string>) => {
      const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
      return request<ApiResponse<TaskRecord[]>>(`/tasks${qs}`);
    },
    get: (id: string) => request<ApiResponse<TaskRecord>>(`/tasks/${id}`),
    create: (body: TaskCreatePayload) =>
      request<ApiResponse<TaskRecord>>('/tasks', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: TaskUpdatePayload) =>
      request<ApiResponse<TaskRecord>>(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) => request<DeleteResponse>(`/tasks/${id}`, { method: 'DELETE' }),
    complete: (id: string) => request<ApiResponse<TaskRecord>>(`/tasks/${id}/complete`, { method: 'POST' }),
    schedule: (id: string, start: string, end: string) =>
      request<ApiResponse<TaskRecord>>(`/tasks/${id}/schedule`, {
        method: 'POST',
        body: JSON.stringify({ scheduled_start: start, scheduled_end: end }),
      }),
    unschedule: (id: string) => request<ApiResponse<TaskRecord>>(`/tasks/${id}/schedule`, { method: 'DELETE' }),
  },

  events: {
    list: (start?: string, end?: string) => {
      const params: Record<string, string> = {};
      if (start) {
        params.start = start;
      }
      if (end) {
        params.end = end;
      }
      const qs = Object.keys(params).length ? `?${new URLSearchParams(params).toString()}` : '';
      return request<ApiResponse<EventRecord[]>>(`/events${qs}`);
    },
    get: (id: string) => request<ApiResponse<EventRecord>>(`/events/${id}`),
    create: (body: EventWritePayload) =>
      request<ApiResponse<EventRecord>>('/events', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<EventWritePayload>) =>
      request<ApiResponse<EventRecord>>(`/events/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) => request<DeleteResponse>(`/events/${id}`, { method: 'DELETE' }),
  },

  dailyNotes: {
    list: (params?: Record<string, string>) => {
      const qs = params ? `?${new URLSearchParams(params).toString()}` : '';
      return request<ApiResponse<DailyNoteRecord[]>>(`/daily-notes${qs}`);
    },
    get: (date: string) => request<ApiResponse<DailyNoteRecord | null>>(`/daily-notes/${date}`),
    upsert: (date: string, body: DailyNoteWritePayload) =>
      request<ApiResponse<DailyNoteRecord | null>>(`/daily-notes/${date}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    append: (date: string, text: string) =>
      request<ApiResponse<DailyNoteRecord | null>>(`/daily-notes/${date}/append`, {
        method: 'POST',
        body: JSON.stringify({ text }),
      }),
  },

  categories: {
    list: () => request<ApiResponse<CategoryRecord[]>>('/categories'),
    create: (body: { name: string; icon?: string; color?: string }) =>
      request<ApiResponse<CategoryRecord>>('/categories', { method: 'POST', body: JSON.stringify(body) }),
  },

  auth: {
    me: () => request<ApiResponse<AuthState>>('/auth/me'),
    bootstrap: (username: string, password: string) =>
      request<AuthBootstrapResponse>('/auth/bootstrap', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      }),
    loginPassword: (username: string, password: string) =>
      request<AuthPasswordResponse>('/auth/login/password', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      }),
    getPasskeyRegistrationOptions: () =>
      request<ApiResponse<import('./webauthn').PublicKeyCredentialCreationOptionsJSON>>('/auth/passkeys/register/options', { method: 'POST' }),
    verifyPasskeyRegistration: (body: unknown) =>
      request<AuthPasskeyVerificationResponse>('/auth/passkeys/register/verify', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    getPasskeyAuthenticationOptions: () =>
      request<ApiResponse<import('./webauthn').PublicKeyCredentialRequestOptionsJSON>>('/auth/passkeys/authenticate/options', { method: 'POST' }),
    verifyPasskeyAuthentication: (body: unknown) =>
      request<AuthPasskeyVerificationResponse>('/auth/passkeys/authenticate/verify', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    listPasskeys: () => request<ApiResponse<PasskeyInfo[]>>('/auth/passkeys'),
    deletePasskey: (id: string) =>
      request<ApiResponse<{ success: boolean }>>(`/auth/passkeys/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    logout: () => request<ApiResponse<{ success: boolean }>>('/auth/logout', { method: 'POST' }),
  },

  health: () => request<ApiResponse<{ status: string; version: string; system: string; timestamp: string }>>('/health'),
};
